/**
 * Firebase / native-SDK auth errors → copy we're willing to show a person.
 *
 * Lives apart from AuthContext so it can be tested without standing up the
 * firebase module graph, and so there is ONE place that decides how an auth
 * failure is worded.
 *
 * The rule: users get plain language, engineers get the details. Every caller
 * logs the raw error to Sentry with its code, message and platform tags before
 * calling this, so nothing here needs to leak diagnostics to be debuggable.
 * (Until 2026-07-21 the NATIVE Google/Apple paths rendered
 * `Sign-in failed [code]: <raw message or JSON.stringify(err)>` straight into
 * the auth screen's error box — which also made a tapped "Cancel" look like a
 * crash.)
 */
export function getAuthErrorMessage(err: { code?: string; message?: string }): string {
    const code = err?.code ?? '';
    const messages: Record<string, string> = {
        'auth/user-not-found': 'No account found with this email. Create an account to get started.',
        'auth/invalid-credential': 'Invalid email or password. Please try again.',
        'auth/wrong-password': 'Incorrect password. Please try again.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/user-disabled': 'This account has been disabled. Please contact support.',
        'auth/email-already-in-use': 'An account already exists with this email. Try signing in instead.',
        'auth/weak-password': 'Password is too weak. Use at least 6 characters.',
        'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
        'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
        'auth/network-request-failed': 'Network error. Please check your connection and try again.',
        'auth/popup-closed-by-user': 'Sign-in was cancelled.',
        'auth/cancelled-popup-request': 'Sign-in was cancelled.',
        'auth/account-exists-with-different-credential': 'An account already exists with this email. Try signing in instead.',
        'auth/requires-recent-login': 'Please sign in again to continue.',
        'auth/expired-action-code': 'This reset link has expired. Please request a new one.',
        'auth/invalid-action-code': 'This reset link is invalid. Please request a new one.',
        'auth/missing-email': 'Please enter your email address.',
        'auth/credential-already-in-use': 'This account is already linked to another user. Try signing in instead.',
        'auth/provider-already-linked': 'This sign-in method is already linked to your account.',
        // Native cancellations. @capacitor-firebase/authentication surfaces the
        // STORE SDK's error here, not a Firebase `auth/*` code: Android Google
        // Sign-In returns 12501, Apple's ASAuthorizationError.canceled is 1001.
        '12501': 'Sign-in was cancelled.',
        '1001': 'Sign-in was cancelled.',
    };
    if (messages[code]) return messages[code];

    // Cancelling isn't a failure, and the native SDKs word it a dozen ways
    // ("The user canceled the sign-in flow.", "Sign in cancelled.", …) under
    // codes we can't enumerate. Match the intent rather than the spelling —
    // both spellings of "cancel(l)ed" included.
    if (/cancel/i.test(err?.message ?? '')) return 'Sign-in was cancelled.';

    return 'Something went wrong. Please try again.';
}
